import { z } from "zod";
import { objectIdSchema } from "./common.js";
/**
 * Dispatch (D-20). Stock is the unused reward codes a holder has; a dispatch
 * moves codes from one holder to the next. Only batches printed while the
 * module is on are tracked.
 */
/** Who can hold stock. `org` is the factory and has no id. */
export const HOLDER_TYPES = ["org", "depot", "distributor", "sub_distributor", "counter"];
export const HOLDER_LABELS = {
    org: "Factory",
    depot: "Depot",
    distributor: "Distributor",
    sub_distributor: "Sub-distributor",
    counter: "Counter",
};
export const holderSchema = z
    .object({ type: z.enum(HOLDER_TYPES), id: objectIdSchema.nullable() })
    .refine((h) => (h.type === "org") === (h.id === null), { message: "Choose who holds the stock." });
const scannedCode = z
    .string()
    .trim()
    .min(4, "Enter a code")
    .max(40)
    .transform((s) => s.toUpperCase());
/** One way of adding codes to a dispatch. Every kind resolves to a set of codes. */
export const dispatchLineSchema = z.discriminatedUnion("kind", [
    /** A whole print run: picked from the list (`batchId`) or scanned from the sheet header (`code`). */
    z
        .object({ kind: z.literal("batch"), batchId: objectIdSchema.optional(), code: scannedCode.optional() })
        .refine((l) => Boolean(l.batchId || l.code), { message: "Choose a batch." }),
    z.object({ kind: z.literal("carton"), code: scannedCode }),
    z
        .object({
        kind: z.literal("range"),
        batchId: objectIdSchema,
        from: z.number().int().min(1),
        to: z.number().int().min(1),
    })
        .refine((l) => l.to >= l.from, { message: "The series ends before it starts.", path: ["to"] }),
    z.object({ kind: z.literal("code"), code: scannedCode }),
]);
export const previewDispatchSchema = z.object({
    /** Only someone with an organization-wide grant picks the sender. */
    from: holderSchema.optional(),
    lines: z.array(dispatchLineSchema).min(1).max(200),
});
export const createDispatchSchema = previewDispatchSchema.extend({ to: holderSchema });
/**
 * What a scanned QR is. Stickers open `/{org}/c/{code}`, cartons
 * `/{org}/k/{code}` and batch sheets `/{org}/b/{code}`; a typed code is a sticker.
 */
export function scanTarget(raw) {
    const text = raw.trim();
    let path = text;
    if (/^https?:\/\//i.test(text)) {
        try {
            path = new URL(text).pathname;
        }
        catch {
            path = text;
        }
    }
    const match = /\/([ckb])\/([A-Za-z0-9-]+)\/?$/.exec(path);
    if (match) {
        const type = match[1] === "k" ? "carton" : match[1] === "b" ? "batch" : "code";
        return { type, value: match[2].toUpperCase() };
    }
    return { type: "code", value: text.toUpperCase() };
}
//# sourceMappingURL=dispatch.js.map